import React, { useState, useEffect } from 'react';
import './App.css';
import {
  medalMap,
  loadFromLocalStorage,
  loadRawLocalStorage,
  saveToLocalStorage,
  STORAGE_KEYS,
  migrateLegacyLocalStorage,
  stripAnswersFromGame,
  stripAnswersFromHistory,
  getOrCreateClientId,
  calculateStats,
  debugLog
} from './utils';

import {
  handleKeyPress,
  handleSubmit,
  handleDelete,
  handleSkillUp,
  handleShare
} from './eventHandlers';

import {
  createNewGame,
  addGuessToLevel,
  addLevelToGame,
  finalizeLevel,
} from './gameData';

import {
  getServerCodes,
  getDailyCodes,
  getESTDateString
} from "./dailyCodes";

import Toolbar from './Toolbar';
import SplashPage from './SplashPage';
import Keyboard from './Keyboard';
import WelcomeBackModal from './WelcomeBackModal';
import SettingsModal from './SettingsModal';
import GameBoard, { getEndOfGuessDelayMs } from './GameBoard';
import HelpMenuModal from './HelpMenuModal';
import HelpModal from './HelpModal';
import HexModal from './HexModal';
import AboutModal from './AboutModal';
import ShareModal from './ShareModal';
import StatsModal from './StatsModal';
import HamburgerMenu from './HamburgerMenu';
import ContactModal from './ContactModal';
import ReportBugModal from './ReportBugModal';
import PrivacyModal from './PrivacyModal';

//setDebug(disabled);

const firstLevel = 0;
const maxLevel = 2;

const helperTextList = [
  'Unique digits in Ascending Order',
  'Unique digits in Ascending or Descending Order',
  'Digits may repeat, Ascending or Descending Order'
];

const datePart = (iso) => (typeof iso === 'string' ? iso.slice(0, 10) : null);
const PROD_HOST = 'crackle.theaiwhisperer.agency';
const PROD_URL = `https://${PROD_HOST}`;
const isNativeRuntime = () =>
  typeof window !== 'undefined' &&
  (!!window.Capacitor || window.location.protocol === 'capacitor:');
const telemetryApi = (path) => (isNativeRuntime() ? `${PROD_URL}${path}` : path);

const injectAnswersIntoGame = (game, bundle) => {
  if (!game || !Array.isArray(game.levels)) return game;
  const levels = game.levels.map(level => {
    const meta = bundle?.levels?.[level.level];
    if (!meta || !meta.code) {
      throw new Error(`Missing code for level ${level.level}`);
    }
    return { ...level, answer: String(meta.code).toUpperCase() };
  });
  return { ...game, levels };
};

const isProdTelemetry = () => {
  if (typeof window === 'undefined') return false;
  if (process.env && process.env.NODE_ENV !== 'production') return false;
  return window.location.hostname === PROD_HOST || isNativeRuntime();
};
const generateSessionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sess_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
};
const getPlatformLabel = () => {
  if (typeof window === 'undefined') return null;
  if (window.Capacitor?.getPlatform) return window.Capacitor.getPlatform();
  if (window.Capacitor || window.location.protocol === 'capacitor:') return 'native';
  return 'web';
};
const getLocaleLabel = () =>
  (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : null;

const buildLevelPayloads = (history, clientId) => {
  if (!Array.isArray(history) || !clientId) return [];
  const payloads = [];
  const platform = getPlatformLabel();
  const locale = getLocaleLabel();
  history.forEach((game) => {
    if (!game || !Array.isArray(game.levels)) return;
    const dateUTC = game.dateUTC || game.dateISO || (game.timestamp ? new Date(game.timestamp).toISOString().slice(0, 10) : null);
    if (!dateUTC) return;
    const sessionId = game.sessionId || (game.timestamp ? `sess_${game.timestamp}` : null);
    game.levels.forEach((level, idx) => {
      if (!level || !Array.isArray(level.guesses)) return;
      if (level.guesses.length === 0) return;
      const levelIndex = Number.isInteger(level.level) ? level.level : idx;
      const guessesUsed = level.guesses.length;
      const guessesAllowed = level.guessesAllowed ?? (game.hardMode ? levelIndex + 4 : 6);
      const won = level.completed === true;
      const durationMs = (level.levelStartTime && level.levelEndTime)
        ? Math.max(0, level.levelEndTime - level.levelStartTime)
        : null;
      payloads.push({
        client_id: clientId,
        session_id: sessionId,
        date: dateUTC,
        level: levelIndex,
        hard_mode: !!game.hardMode,
        guesses_used: guessesUsed,
        guesses_allowed: guessesAllowed,
        won,
        duration_ms: durationMs,
        platform,
        locale,
      });
    });
  });
  return payloads;
};

function App() {

  // Overall Game variables
  const [gameOn, setGameOn] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [currentGame, setCurrentGame] = useState(null);

  const [hardMode, setHardMode] = useState(() =>
    loadRawLocalStorage(STORAGE_KEYS.settings.hardMode, 'false') === 'true'
  );
  const [darkMode, setDarkMode] = useState(() =>
    loadRawLocalStorage(STORAGE_KEYS.settings.darkMode, 'false') === 'true'
  );
  const [gameHistory, setGameHistory] = useState(() =>
    loadFromLocalStorage(STORAGE_KEYS.game.history, [])
  );

  const getMaxGuesses = () => (hardMode ? level + 4 : 6);

  const [level, setLevel] = useState(0);
  const [answer, setAnswer] = useState(null);
  const [guesses, setGuesses] = useState(Array(6).fill([]));
  //const [shareSummary, setShareSummary] = useState([]);
  const [shareLevelsCleared, setShareLevelsCleared] = useState(0);
  const [shareTotalMaxGuesses, setShareTotalMaxGuesses] = useState(0);
  //const [guessesUsedAtLevelStart, setGuessesUsedAtLevekStart] = useState(0);
  const [currentRow, setCurrentRow] = useState(0);
  const [lockedGreens, setLockedGreens] = useState(Array(5).fill(null));
  const [feedback, setFeedback] = useState(Array(6).fill(Array(5).fill(null)));
  const [displayedFeedback, setDisplayedFeedback] = useState(Array(6).fill(Array(5).fill(null)));
  //const [shareText, setShareText] = useState('');
  const [didWin, setDidWin] = useState(false);
  //const [summaryBlocks, setSummaryBlocks] = useState([]);
  //const [showGameBoard, setShowGameBoard] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [shareSummaryBlocks, setShareSummaryBlocks] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showHexInfo, setShowHexInfo] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [clipboardMessage, setClipboardMessage] = useState('');
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [dailyBundle, setDailyBundle] = useState(null);   // server payload
  const [bundleLoading, setBundleLoading] = useState(true);
  const [bundleError, setBundleError] = useState(null);
  const [animationMode, setAnimationMode] = useState(() =>
    loadRawLocalStorage(STORAGE_KEYS.settings.animationMode, 'slide')
  );
  const [animateRowIndex, setAnimateRowIndex] = useState(null);
  const [showEndMessage, setShowEndMessage] = useState(true);
  const [pendingEndMessageDelay, setPendingEndMessageDelay] = useState(false);
  const pendingEndMessageRef = React.useRef(false);
  const justSubmittedRef = React.useRef(false);
  const [showMenu, setShowMenu] = useState(false);
  const [menuCoords, setMenuCoords] = useState({ top: 60, left: 10 });
  const [showContactModal, setShowContactModal] = useState(false);
  const [showReportBugModal, setShowReportBugModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // Dev/backdoor: restart game with a randomly selected past bundle.
  function isDevMode() {
    try {
      if (typeof window === 'undefined') return false;
      const host = window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1') return true;
      if (localStorage.getItem(STORAGE_KEYS.flags.dev) === '1') return true;
      // Allow if running in development mode
      if (process.env && process.env.NODE_ENV === 'development') return true;
    } catch (e) { }
    return false;
  }

  async function handleBackdoorRequest() {
    if (!isDevMode()) {
      console.warn('Backdoor request ignored: not in dev mode');
      return;
    }
    console.log('[App] Backdoor request accepted (dev mode)');
    try {
      // Try a few times to get a historical date that the server actually has.
      const maxAttempts = 8;
      const maxDaysBack = 180; // pick within last 180 days
      let bundle = null;
      let tried = 0;
      let chosenDate = null;
      setBundleLoading(true);
      setBundleError(null);
      while (tried < maxAttempts) {
        tried += 1;
        const offset = 1 + Math.floor(Math.random() * Math.max(1, maxDaysBack)); // 1..maxDaysBack
        const msPerDay = 24 * 60 * 60 * 1000;
        const target = new Date(Date.now() - offset * msPerDay);
        const targetDateUTC = getESTDateString(target);
        console.log(`[App] Backdoor attempt ${tried}: trying offset=${offset}, targetDateUTC=${targetDateUTC}`);
        // Request bundle for that exact date. When running locally, the app's
        // default API base is relative (localhost) which may not have historical
        // bundles; prefer the production API for historical fetches during dev.
        const isLocalhost = (typeof window !== 'undefined') && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        if (isLocalhost) {
          try {
            const PROD_BASE = 'https://crackle.theaiwhisperer.agency';
            const res = await fetch(`${PROD_BASE}/api/get_codes?date=${encodeURIComponent(targetDateUTC)}`, { method: 'GET' });
            if (!res.ok) {
              console.log(`[App] Prod fetch returned status=${res.status} for ${targetDateUTC}`);
              bundle = null;
            } else {
              const data = await res.json();
              // Normalize server response into the bundle shape expected by the app
              const normalized = {
                isoDateUTC: (data.date || targetDateUTC).slice(0, 10),
                levels: {
                  0: { code: String(data.l1 || data.l1_code || '').toUpperCase(), dir: null, pr: data.l1_pr4 ?? null, mean: data.l1_mean ?? null, target: null },
                  1: { code: String(data.l2 || data.l2_code || '').toUpperCase(), dir: data.l2_dir ?? null, pr: data.l2_pr5 ?? null, mean: data.l2_mean ?? null, target: data.l2_target ?? null },
                  2: { code: String(data.l3 || data.l3_code || '').toUpperCase(), dir: data.l3_dir ?? null, pr: data.l3_pr6 ?? null, mean: data.l3_mean ?? null, target: data.l3_target ?? null }
                },
                global: {}
              };
              // Basic validation: codes should match 5 hex chars
              const codeRx = /^[0-9A-F]{5}$/i;
              if (codeRx.test(normalized.levels[0].code) && codeRx.test(normalized.levels[1].code) && codeRx.test(normalized.levels[2].code)) {
                bundle = normalized;
              } else {
                console.log('[App] Prod fetch returned invalid codes for date', targetDateUTC, normalized);
                bundle = null;
              }
            }
          } catch (err) {
            console.error('[App] Prod fetch error', err);
            bundle = null;
          }
        } else {
          // Not localhost: use existing client helper which respects API_BASE
          bundle = await getDailyCodes(targetDateUTC);
        }
        console.log('[App] Backdoor fetched bundle (attempt ' + tried + '):', bundle?.isoDateUTC, bundle?.levels && { l1: bundle.levels[0]?.code, l2: bundle.levels[1]?.code, l3: bundle.levels[2]?.code });
        if (bundle && bundle.isoDateUTC === targetDateUTC) {
          chosenDate = targetDateUTC;
          break; // success: server returned the requested historical date
        }
        // If server fell back to today (or another date), try again.
        console.log(`[App] Backdoor attempt ${tried} did not return requested date (requested ${targetDateUTC}, got ${bundle?.isoDateUTC}). Retrying...`);
        // small delay between attempts
        await new Promise((r) => setTimeout(r, 100));
      }

      if (!bundle) throw new Error('No bundle returned after attempts');
      if (!chosenDate) {
        console.warn('[App] Backdoor could not find a historical bundle after attempts; using returned bundle (may be today).');
      } else {
        console.log('[App] Backdoor selected historical date:', chosenDate);
      }
      setDailyBundle(bundle);
  // Clear any saved `currentGame` so the app does not rehydrate the previous session.
  console.log('[App] Backdoor clearing saved currentGame to force fresh start');
  try { localStorage.removeItem(LS_CURRENT_GAME); } catch (e) { /* ignore */ }
  setCurrentGame(null);
  setShowWelcomeBack(false);
  // Reset UI flags that could cause rehydration to take precedence
  setGameOn(false);
  setGameOver(false);
  setDidWin(false);
      // Close contact modal and start game using the fetched bundle
      setShowContactModal(false);
      // Give a small delay to ensure dailyBundle is set before starting
      await new Promise((r) => setTimeout(r, 50));
      await handleStartFromSplash();
    } catch (e) {
      console.error('Backdoor failed', e);
      setBundleError(e?.message || 'Backdoor failed');
    } finally {
      setBundleLoading(false);
    }
  }

  // TESTMODE toggle: true = local generator, false = server daily
  //const TESTMODE = loadRawLocalStorage(STORAGE_KEYS.flags.testMode, 'false') === 'true'; 
  const TESTMODE = false;

  const LS_CURRENT_GAME = STORAGE_KEYS.game.current;

  function todayISO() {
    return getESTDateString();
  }

  function renderShareButton(props) {
    const {
      currentGame,
      setShareSummaryBlocks,
      setShareLevelsCleared,
      setShareTotalMaxGuesses,
      setShowShareModal,
      setClipboardMessage
    } = props;

    return (
      <button
        onClick={() =>
          handleShare(
            currentGame,
            setShareSummaryBlocks,
            setShareLevelsCleared,
            setShareTotalMaxGuesses,
            setShowShareModal,
            setClipboardMessage
          )
        }
        className="skill-up-button visible"
      >
        Share
      </button>
    );
  }

  // Prefetch today's codes on mount
  useEffect(() => {
    if (!showSplash) return; // only prefetch while splash is visible
    let cancelled = false;
    (async () => {
      setBundleLoading(true);
      setBundleError(null);
      try {
        // Always fetch TODAY on splash; TESTMODE date is chosen on click
        // Enforce 1s minimum loading time for splash experience
        const [bundle] = await Promise.all([
          getServerCodes(false),
          new Promise(r => setTimeout(r, 2000))
        ]);
        if (!cancelled) setDailyBundle(bundle);
      } catch (e) {
        if (!cancelled) setBundleError(e?.message || 'Failed to load today’s codes');
      } finally {
        if (!cancelled) setBundleLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showSplash]);

  // Persist gameHistory changes
  const isHistoryFirstMount = React.useRef(true);
  useEffect(() => {
    if (isHistoryFirstMount.current) {
      isHistoryFirstMount.current = false;
      return;
    }
    saveToLocalStorage(STORAGE_KEYS.game.history, stripAnswersFromHistory(gameHistory));
  }, [gameHistory]);

  const telemetrySentRef = React.useRef(new Map());
  useEffect(() => {
    if (!isProdTelemetry()) return;
    const clientId = getOrCreateClientId();
    const payloads = buildLevelPayloads(gameHistory, clientId);
    payloads.forEach((payload) => {
      const key = `${payload.client_id}|${payload.date}|${payload.level}`;
      const prev = telemetrySentRef.current.get(key);
      if (prev && prev.guesses_used === payload.guesses_used && prev.won === payload.won) {
        return;
      }
      telemetrySentRef.current.set(key, { guesses_used: payload.guesses_used, won: payload.won });
      const logSecret = process.env.REACT_APP_LOG_SECRET;
      if (!logSecret) {
        return;
      }
      fetch(telemetryApi('/api/log_level_result'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-log-secret': logSecret,
        },
        body: JSON.stringify(payload),
      }).catch(() => {});
    });
  }, [gameHistory]);

  // Persist hardMode changes
  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.settings.hardMode, hardMode);
  }, [hardMode]);

  useEffect(() => {
    // Update document body class
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }

    // Save darkMode to localStorage
    saveToLocalStorage(STORAGE_KEYS.settings.darkMode, darkMode);

  }, [darkMode]);

  useEffect(() => {
    migrateLegacyLocalStorage();
  }, []);

  useEffect(() => {
    debugLog('[App] showSplash =', showSplash);
  }, [showSplash]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const savedGame = loadFromLocalStorage(STORAGE_KEYS.game.current, null);
      if (!savedGame) return;
      if (!savedGame.sessionId) {
        savedGame.sessionId = generateSessionId();
        saveToLocalStorage(STORAGE_KEYS.game.current, savedGame);
      }

      const savedDate = datePart(savedGame.dateUTC || savedGame.dateISO);
      const today = todayISO();

      if (savedDate !== today) {
        // stale session - do NOT rehydrate; keep splash; no modal
        setShowWelcomeBack(false);
        return;
      }

      try {
        const bundle = await getDailyCodes(savedDate);
        if (cancelled) return;

        setDailyBundle(bundle);
        const rehydrated = injectAnswersIntoGame(savedGame, bundle);
        setCurrentGame(rehydrated);

        if (typeof rehydrated.hardMode === 'boolean') {
          setHardMode(rehydrated.hardMode);
        }

        const firstLevelData = rehydrated.levels?.[0] || null;
        const hasMadeFirstGuess = !!(firstLevelData?.guesses?.length);
        if (hasMadeFirstGuess) setGameOn(true);

        setShowWelcomeBack(true);
      } catch (e) {
        if (cancelled) return;
        setShowWelcomeBack(false);
        setBundleError('Unable to resume without a connection. Please retry.');
      }
    })();
    return () => { cancelled = true; };
  }, []);


  // This useEffect listens to currentGame state changes and saves it to localStorage.
  // It also removes the item from localStorage if currentGame is set to null.
  useEffect(() => {

    if (currentGame === null || currentGame === undefined) {
      // If currentGame is explicitly set to null (e.g., when starting a truly new session after completion)
      localStorage.removeItem(STORAGE_KEYS.game.current); // Correctly use native method to clear save
    } else {
      // Check if the game state is past the point of the first guess in the first level
      // Access the first level data safely
      //const firstLevelData = currentGame.levels && currentGame.levels.length > 0 ? currentGame.levels[0] : null;

      // Check if the first level exists and has at least one guess
      //const hasMadeFirstGuess = firstLevelData && firstLevelData.guesses && firstLevelData.guesses.length > 0;

      //if (hasMadeFirstGuess) {
      saveToLocalStorage(STORAGE_KEYS.game.current, stripAnswersFromGame(currentGame));
      //} else {
      // This prevents saving the initial 0-guess state created by startNewGame
      //}
    }
  }, [currentGame]); // Keep dependency on currentGame

  const keyStates = React.useMemo(() => {
    const states = {};
    const priority = { green: 3, yellow: 2, gray: 1 };
    guesses.forEach((guess, rowIdx) => {
      if (!Array.isArray(guess) || guess.length === 0) return;
      const rowFeedback = displayedFeedback[rowIdx];
      if (!Array.isArray(rowFeedback)) return;
      guess.forEach((digit, colIdx) => {
        const color = rowFeedback[colIdx];
        if (!color) return;
        const key = digit.toUpperCase();
        if (!states[key] || priority[color] > priority[states[key]]) {
          states[key] = color;
        }
      });
    });
    return states;
  }, [guesses, displayedFeedback]);

  const stats = calculateStats(gameHistory);
  const animateOnSubmitOnly = isProdTelemetry();
  const goldMedalCount = stats?.total?.medals?.gold?.count || 0;

  useEffect(() => {
    if (!justSubmittedRef.current) {
      setDisplayedFeedback(feedback);
      return;
    }
    justSubmittedRef.current = false;
    const delay = getEndOfGuessDelayMs(animationMode);
    const id = setTimeout(() => setDisplayedFeedback(feedback), delay);
    return () => clearTimeout(id);
  }, [feedback, animationMode]);
  const shouldShowEndMessage =
    gameOver &&
    showEndMessage &&
    !pendingEndMessageDelay &&
    !pendingEndMessageRef.current;

  useEffect(() => {
    if (!gameOver) {
      setShowEndMessage(true);
      setPendingEndMessageDelay(false);
      pendingEndMessageRef.current = false;
      return;
    }
    const shouldDelay =
      !animateOnSubmitOnly || pendingEndMessageDelay;
    const delayMs = shouldDelay ? getEndOfGuessDelayMs(animationMode) : 0;
    setShowEndMessage(delayMs === 0);
    if (delayMs === 0) {
      setPendingEndMessageDelay(false);
      pendingEndMessageRef.current = false;
      return;
    }
    const timeoutId = setTimeout(() => {
      setShowEndMessage(true);
      setPendingEndMessageDelay(false);
      pendingEndMessageRef.current = false;
    }, delayMs);
    return () => clearTimeout(timeoutId);
  }, [gameOver, animationMode, animateOnSubmitOnly, pendingEndMessageDelay]);

  useEffect(() => {
    if (!animateOnSubmitOnly) return;
    if (showWelcomeBack) {
      setAnimateRowIndex(null);
      setPendingEndMessageDelay(false);
      pendingEndMessageRef.current = false;
    }
  }, [animateOnSubmitOnly, showWelcomeBack]);

  useEffect(() => {
    if (!animateOnSubmitOnly) return;
    const allowTetris = goldMedalCount >= 10;
    const allowMatrix = goldMedalCount >= 25;
    if (
      (!allowTetris && animationMode === 'tetris') ||
      (!allowMatrix && animationMode === 'matrix')
    ) {
      setAnimationMode('slide');
      saveToLocalStorage(STORAGE_KEYS.settings.animationMode, 'slide');
    }
  }, [animateOnSubmitOnly, animationMode, goldMedalCount]);

  //const { guessesUsed, guessesAllowed } = getCurrentScore(gameHistory);

  async function retryFetchToday() {
    setBundleError(null);
    setBundleLoading(true);
    try {
      const [bundle] = await Promise.all([
        getServerCodes(false),
        new Promise(r => setTimeout(r, 2000))
      ]);
      setDailyBundle(bundle);
    } catch (e) {
      setBundleError(e?.message || 'Failed to load today’s codes');
    } finally {
      setBundleLoading(false);
    }
  }

  // Function to handle starting the game from the splash page
  async function handleStartFromSplash() {
    try {
      let bundle = dailyBundle;

      if (TESTMODE) {
        // choose a random historical date at click time
        bundle = await getServerCodes(true);
      } else {
      }

      if (!bundle) throw new Error('No daily bundle available');

      // Initialize Level 1 using server meta
      const meta1 = bundle.levels[0];
      const code = meta1.code.toUpperCase();
      //      const order = (meta1.dir || 'ascending').toLowerCase().startsWith('desc')
      //        ? 'descending'
      //        : 'ascending';
      setAnswer(code);
      const maxGuessesForLevel = hardMode ? (firstLevel + 4) : 6;
      setGuesses(Array(maxGuessesForLevel).fill([]));
      setFeedback(Array(maxGuessesForLevel).fill(Array(5).fill(null)));

      // Make sure your game state knows which date this run belongs to
      //setCurrentGame(prev =>
      //  addLevelToGame(
      //    {
      //      ...prev,
      //      dateUTC: bundle.isoDateUTC,
      //      pendingSkillUp: false,
      //      gameFinished: false,
      //    },
      //    1, code, prev?.hardMode ?? false
      //  )
      //);

      setCurrentGame(prev => {
        // If this is the very first run, build a valid base game object.
        const base = (prev && Array.isArray(prev.levels))
          ? prev
          : createNewGame(!!hardMode); // your createNewGame should return { hardMode, levels: [], ... }

        // Idempotence: if Level 1 already exists, don’t add it again (e.g., double-click).
        if (Array.isArray(base.levels) && base.levels.length > 0) {
          return {
            ...base,
            dateUTC: base.dateUTC ?? bundle.isoDateUTC,
            pendingSkillUp: false,
            gameFinished: false,
          };
        }

        // First time: append Level 1.
        return addLevelToGame(
          {
            ...base,
            dateUTC: bundle.isoDateUTC,
            pendingSkillUp: false,
            gameFinished: false,
          },
          0,            // level index
          code,         // secret string
          base.hardMode // keep from base
        );
      });

      // Hide splash per your existing mechanism
      setShowSplash(false);
    } catch (e) {
      setBundleError(e?.message || 'Failed to start game');
    }
  }


  // Function to manage hard mode state changes
  const handleHardModeToggle = (newHardMode) => {
    setHardMode(newHardMode); // Update App.js's own hardMode state

    if (!gameOn && currentGame && currentGame.levels?.[0]?.guesses?.length === 0) {
      // Game exists, 0 guesses made, gameOn is false

      const newGuessesAllowed = newHardMode ? 4 : 6;

      const updatedFirstLevel = {
        ...currentGame.levels[0],
        guessesAllowed: newGuessesAllowed,
      };

      const updatedCurrentGame = {
        ...currentGame,
        hardMode: newHardMode,
        levels: [updatedFirstLevel, ...currentGame.levels.slice(1)],
      };

      setCurrentGame(updatedCurrentGame);

      setGuesses(Array(newGuessesAllowed).fill([]));
      setFeedback(Array(newGuessesAllowed).fill(Array(5).fill(null)));
    }
  };


  const buildSingleBlock = (feedback) => {
    const emojiMap = {
      green: '🟩',
      yellow: '🟨',
      gray: '⬜',
      default: '⬛'
    };

    return feedback
      .filter(row => row.some(color => color !== null))
      .map(row => row.map(color => emojiMap[color || 'default']).join(''))
      .join('\n');
  };

  const handleContinueGame = () => {
    if (currentGame) {
      restoreGameFromCurrentGame(currentGame);
      setShowWelcomeBack(false); // Hide welcome back modal
      setShowSplash(false); // Hide splash (already hidden by logic, but safe)
      // setShowGameBoard(true); // GameBoard renders automatically
      setGameOn(true); // Set gameOn to true after state is restored
    } else {
      // This case should ideally not happen if modal is only shown when currentGame exists
      setShowWelcomeBack(false);

    }
  };

  const restoreGameFromCurrentGame = (game) => {
    debugLog("[App.js] restoreGameFromCurrentGame: Restoring state...");
    // Ensure game and game.levels exist and are not empty
    if (!game || !game.levels || game.levels.length === 0) {
      // This indicates a serious issue with the saved state.
      // It's safer to discard the bad state and start a new game clean.
      localStorage.removeItem(STORAGE_KEYS.game.current); // Clear corrupted save
      //startNewGame(); // Start a new game as if no save was found
      return;
    }

    // Use the last level entry in the saved game object
    const lastLevelData = game.levels[game.levels.length - 1];
    const currentRestoredLevelIdx = lastLevelData.level;

    // Restore core level state
    setLevel(currentRestoredLevelIdx);
    setAnswer(lastLevelData.answer); // Always set the answer for the current level

    // Determine max guesses for the current level for UI representation
    const maxGuessesForThisLevel = game.hardMode ? currentRestoredLevelIdx + 4 : 6;

    // Common setup for displaying guesses and feedback for the *current* level
    const actualGuessesMade = lastLevelData.guesses.map(entry => entry.guess);
    const actualFeedbackGiven = lastLevelData.guesses.map(entry => entry.feedback);

    const displayGuesses = Array(maxGuessesForThisLevel).fill(null).map((_, index) =>
      index < actualGuessesMade.length ? actualGuessesMade[index] : []
    );
    const displayFeedback = Array(maxGuessesForThisLevel).fill(null).map((_, index) =>
      index < actualFeedbackGiven.length ? actualFeedbackGiven[index] : Array(5).fill(null)
    );

    setGuesses(displayGuesses); // Sets guesses for the *current level*
    setFeedback(displayFeedback); // Sets feedback for the *current level*
    setCurrentRow(actualGuessesMade.length); // Set current row based on guesses already made

    // Restore locked greens for hard mode
    const lastActualGuessMade = actualGuessesMade.at(-1) || [];
    const lastActualFeedbackReceived = actualFeedbackGiven.at(-1) || [];
    const newLockedGreens = Array(5).fill(null);
    if (game.hardMode && lastActualFeedbackReceived.length > 0) { // Only apply locked greens in hard mode
      lastActualFeedbackReceived.forEach((color, idx) => {
        if (color === 'green') newLockedGreens[idx] = lastActualGuessMade[idx];
      });
    }
    setLockedGreens(newLockedGreens);

    // DYNAMIC STATE RESTORATION
    // Determine the state (Won, Lost, In-Progress) based on the actual guesses data.
    // This fixes rehydration bugs where flags like pendingSkillUp were missing.

    const lastFeedbackRow = actualFeedbackGiven.length > 0 ? actualFeedbackGiven[actualFeedbackGiven.length - 1] : [];
    const isLevelWon = lastFeedbackRow.length === 5 && lastFeedbackRow.every(c => c === 'green');
    const isLevelLost = !isLevelWon && actualGuessesMade.length >= maxGuessesForThisLevel;

    if (isLevelWon) {
      if (currentRestoredLevelIdx < maxLevel) {
        setDidWin(true);
        setGameOver(true);
        setGameOn(true);
      } else {
        setDidWin(true);
        setGameOver(true);
        setGameOn(false);
      }

      // PATCH: Ensure currentGame has the correct completion flags so Share works.
      // If we found the level is won but the data doesn't say so, update it.
      let updateNeeded = false;
      const updatedGame = { ...game, levels: [...game.levels] };
      const updatedLevel = { ...updatedGame.levels[currentRestoredLevelIdx] };

      if (!updatedLevel.completed) {
        updatedLevel.completed = true;
        updatedLevel.crackedOnGuess = actualGuessesMade.length;
        updatedGame.levels[currentRestoredLevelIdx] = updatedLevel;
        updateNeeded = true;
      }

      if (currentRestoredLevelIdx >= maxLevel) {
        if (!updatedGame.overallWin) {
          updatedGame.overallWin = true;
          updatedGame.gameFinished = true;
          updateNeeded = true;
        }
      } else {
        // Level 1 or 2 won, but not skill up clicked? 
        if (!updatedGame.pendingSkillUp) {
          // Technically pendingSkillUp is an App state concept more than data, 
          // but if it helps elsewhere:
          updatedGame.pendingSkillUp = true;
          updateNeeded = true;
        }
      }

      if (updateNeeded) {
        setCurrentGame(updatedGame);
      }
    } else if (isLevelLost) {
      setDidWin(false);
      setGameOver(true);
      setGameOn(false);
    } else {
      setDidWin(false);
      setGameOver(false);
      setGameOn(true);
    }

  };

  useEffect(() => {
    const handleKeyDown = (event) => {
    if (showSplash || showSettings || showHelpMenu || showShareModal || showHexInfo || showHowToPlay || showWelcomeBack || showStats || showReportBugModal) {
        // Also ignore keyboard if WelcomeBackModal is showing
        return; // ignore keyboard input if a modal or splash is active
      }

      const { key } = event;

      if (/^[0-9a-fA-F]$/.test(key)) {
        if (keyStates[key.toUpperCase()] === 'gray') return;
        handleKeyPress(key, guesses, currentRow, setGuesses, gameOver);
      } else if (key === 'Backspace') {
        handleDelete(guesses, currentRow, setGuesses, gameOver);
      } else if (key === 'Enter') {
        justSubmittedRef.current = true;
        if (animateOnSubmitOnly) {
          setAnimateRowIndex(currentRow);
          setPendingEndMessageDelay(true);
          setShowEndMessage(false);
          pendingEndMessageRef.current = true;
        }
        handleSubmit({
          gameOver,
          guesses,
          currentRow,
          setCurrentRow,
          level,
          hardMode,
          feedback,
          answer,
          maxLevel,
          setGameOn,
          setFeedback,
          currentGame,
          setCurrentGame,
          setDidWin,
          setGameOver,
          setGameHistory,
          addGuessToLevel,
          finalizeLevel,
          lockedGreens,
          setLockedGreens,
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    guesses,
    currentRow,
    gameOver,
    hardMode,
    feedback,
    answer,
    level,
    setGameOn,
    setFeedback,
    setCurrentGame,
    setDidWin,
    setGameOver,
    currentGame,
    setGameHistory,
    lockedGreens,
    setLockedGreens,
    setCurrentRow,
    animateOnSubmitOnly,
    setAnimateRowIndex,
    setPendingEndMessageDelay,
    showSplash,
    showSettings,
    showHelpMenu,
    showShareModal,
    showHexInfo,
    showHowToPlay,
    showWelcomeBack, // Add WelcomeBackModal state
    showStats,
    showReportBugModal,
    keyStates
  ]);

  const getHelperText = (ignoreGameOver = false) => {
    if (gameOver && !ignoreGameOver) {
      return '';
    }
    return helperTextList[level] || '';
  };

  // canDelete and canEnter are already correctly derived

  return (
    <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
      {/* Always show Toolbar (except splash) */}
      {!(showSplash || showWelcomeBack) && ( // Hide toolbar if splash OR welcome back is showing
        <Toolbar
          onMenu={(e) => {
            // Compute the button's screen coordinates so the hamburger menu can appear below it
            try {
              const rect = e.currentTarget.getBoundingClientRect();
              setMenuCoords({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
            } catch (err) {
              // fallback
              setMenuCoords({ top: 60, left: 10 });
            }
            setShowMenu(true);
          }}
          onHelp={() => setShowHelpMenu(true)}
          onSettings={() => setShowSettings(true)}
          onStats={() => setShowStats(true)}
        />
      )}

      {/* Hamburger Menu */}
      <HamburgerMenu
        open={showMenu}
        onClose={() => setShowMenu(false)}
        onContact={() => setShowContactModal(true)}
        onReportBug={() => setShowReportBugModal(true)}
        menuCoords={menuCoords}
      />

      {/* Contact Modal */}
      <ContactModal
        open={showContactModal}
        onClose={() => setShowContactModal(false)}
        onBackdoorRequest={handleBackdoorRequest}
      />
      <ReportBugModal
        open={showReportBugModal}
        onClose={() => setShowReportBugModal(false)}
      />

      {/* Show splash under Welcome Back Modal */}
      {showSplash ? ( // If showSplash is true, render this block
        <>
          <SplashPage
            loading={bundleLoading}
            error={bundleError}
            darkMode={darkMode}
            onRetry={retryFetchToday}
            onStart={handleStartFromSplash}
          />
          {showWelcomeBack && ( // WelcomeBackModal renders conditionally ON TOP if showWelcomeBack is also true
            <WelcomeBackModal
              open={showWelcomeBack} // Pass open prop
              onContinue={handleContinueGame} // Use dedicated continue handler
              gameState={currentGame} // Pass currentGame state for modal content
            />
          )}
        </>
      ) : (
        // Otherwise (splash and welcome back are false), show the normal game content
        <>
          <GameBoard
            guesses={guesses}
            feedback={feedback}
            maxGuesses={getMaxGuesses()}
            animationMode={animationMode}
            animateRowIndex={animateRowIndex}
            animateOnSubmitOnly={animateOnSubmitOnly}
          />
          <div className="helper-text-wrapper">
            <div className="helper-text">
              {/* Content for helper text / game over / skill up / share */}
              {gameOver ? (
                shouldShowEndMessage ? (
                  didWin ? (
                    <>
                      <p>{medalMap[level]} You cracked the code!</p>
                      <br />
                      {level < maxLevel ? (
                        <button
                          onClick={() =>
                            handleSkillUp({
                              currentGame,
                              hardMode, // Pass current hardMode
                              level,
                              //                            maxLevel,
                              //                            feedback,
                              finalizeLevel,
                              buildSingleBlock,
                              setShareSummaryBlocks,
                              setLevel,
                              //                            generateAnswer,
                              //                            getAnswer,
                              setAnswer,
                              setGuesses,
                              setFeedback,
                              setCurrentRow,
                              setLockedGreens,
                              setGameOver,
                              setDidWin,
                              setCurrentGame,
                              TESTMODE
                            })
                          }
                          className="skill-up-button visible">
                          Skill Up
                        </button>
                      ) : (
                        // Game won all levels
                        <div className="post-game-button">
                          {renderShareButton({
                            currentGame,
                            setShareSummaryBlocks,
                            setShareLevelsCleared,
                            setShareTotalMaxGuesses,
                            setShowShareModal,
                            setClipboardMessage
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    // Game lost
                    <>
                      &#x274C; Game Over: {answer}
                      <br />
                      <div className="post-game-button">
                        {renderShareButton({
                          currentGame,
                          setShareSummaryBlocks,
                          setShareLevelsCleared,
                          setShareTotalMaxGuesses,
                          setShowShareModal,
                          setClipboardMessage
                        })}
                        {/* Placeholder for a "Start New Game" button here after losing */}
                      </div>
                    </>
                  )
                ) : (
                  // Keep helper text until animations finish.
                  <span>{getHelperText(true)}</span>
                )
              ) : (
                // Game is in progress, show helper text
                <span>{getHelperText()}</span>
              )}
            </div>
          </div>

          <Keyboard
            keyStates={keyStates}
            onKeyPress={(digit) =>
              handleKeyPress(digit, guesses, currentRow, setGuesses, gameOver)
            }
            onSubmit={() => {
              justSubmittedRef.current = true;
              if (animateOnSubmitOnly) {
                setAnimateRowIndex(currentRow);
                setPendingEndMessageDelay(true);
                setShowEndMessage(false);
                pendingEndMessageRef.current = true;
              }
              handleSubmit({
                gameOver,
                guesses,
                currentRow,
                setCurrentRow,
                level,
                hardMode,
                feedback,
                answer,
                maxLevel,
                setGameOn,
                setFeedback,
                currentGame,
                setCurrentGame,
                setDidWin,
                setGameOver,
                setGameHistory,
                addGuessToLevel,
                finalizeLevel,
                lockedGreens,
                setLockedGreens,
              });
            }}
            onDelete={() =>
              handleDelete(
                guesses,
                currentRow,
                setGuesses,
                gameOver
              )
            }
            canSubmit={!gameOver && guesses[currentRow]?.length === 5}
            canDelete={!gameOver && guesses[currentRow]?.length > 0}
          />
        </>
      )}

      <SettingsModal
        open={showSettings}
        gameOn={gameOn}
        currentGame={currentGame}
        hardMode={hardMode}
        onHardModeChange={handleHardModeToggle}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        animationMode={animationMode}
        setAnimationMode={setAnimationMode}
        goldMedalCount={goldMedalCount}
        isProdPlay={animateOnSubmitOnly}
        onClose={() => setShowSettings(false)}
      />

      <ShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        summaryBlocks={shareSummaryBlocks} // Use the state variable updated by handleShare
        levelsCleared={shareLevelsCleared}
        totalMaxGuesses={shareTotalMaxGuesses}
        clipboardMessage={clipboardMessage}
      />

      <StatsModal
        open={showStats}
        onClose={() => setShowStats(false)}
        stats={stats}
      />

      <HelpMenuModal
        open={showHelpMenu}
        onClose={() => setShowHelpMenu(false)}
        onHowToPlay={() => {
          setShowHelpMenu(false);
          setShowHowToPlay(true);
        }}
        onHexInfo={() => {
          setShowHelpMenu(false);
          setShowHexInfo(true);
        }}
        onAbout={() => {
          setShowHelpMenu(false);
          setShowAboutModal(true);
        }}
        onPrivacyPolicy={() => {
          setShowHelpMenu(false);
          setShowPrivacyModal(true);
        }}
      />

      <HelpModal
        open={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
      />

      <HexModal
        open={showHexInfo}
        onClose={() => setShowHexInfo(false)}
      />

      <AboutModal
        open={showAboutModal}
        onClose={() => setShowAboutModal(false)}
      />

      <PrivacyModal
        open={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />

    </div>
  );
}

export default App;
